import { AudioState, AudioAction, Track } from "@/context/audioTypes";

export const initialAudioState: AudioState = {
  currentTrack: null,
  playlist: [],
  originalPlaylist: [],
  currentIndex: -1,
  isPlaying: false,
  isLooping: false,
  isShuffling: false,
  progress: 0,
  duration: 0, // <-- Added duration with a default value of 0
};

export const audioReducer = (state: AudioState, action: AudioAction): AudioState => {
  switch (action.type) {
    case "PLAY_TRACK":
      return {
        ...state,
        currentTrack: action.track,
        playlist: action.playlist,
        originalPlaylist: action.playlist, // Always maintain a copy of the original playlist
        currentIndex: action.playlist.findIndex((t: Track) => t.id === action.track.id),
        isPlaying: true,
        progress: 0,
        // Set duration from track if available or default to 30 for preview URLs or 180 as fallback
        duration: action.track.duration || 
                 (action.track.preview_url ? 30 : 180),
      };

    case "PLAY":
      return { ...state, isPlaying: true };

    case "PAUSE":
      return { ...state, isPlaying: false };

    case "NEXT_TRACK":
      if (state.playlist.length > 0) {
        const nextIndex = (state.currentIndex + 1) % state.playlist.length;
        const nextTrack = state.playlist[nextIndex];
        return {
          ...state,
          currentTrack: nextTrack || null,
          currentIndex: nextIndex,
          isPlaying: true,
          progress: 0,
          // Set duration from next track if available
          duration: nextTrack?.duration || 
                   (nextTrack?.preview_url ? 30 : 180),
        };
      }
      return state;

    case "PREV_TRACK":
      if (state.playlist.length > 0) {
        const prevIndex = (state.currentIndex - 1 + state.playlist.length) % state.playlist.length;
        const prevTrack = state.playlist[prevIndex];
        return {
          ...state,
          currentTrack: prevTrack || null,
          currentIndex: prevIndex,
          isPlaying: true,
          progress: 0,
          // Set duration from prev track if available
          duration: prevTrack?.duration || 
                   (prevTrack?.preview_url ? 30 : 180),
        };
      }
      return state;

    case "TOGGLE_LOOP":
      return { ...state, isLooping: !state.isLooping };

    case "TOGGLE_SHUFFLE":
      if (state.isShuffling) {
        // Disable shuffle: return to the original playlist
        const originalIndex = state.originalPlaylist.findIndex(
          (t) => t.id === state.currentTrack?.id
        );
        return {
          ...state,
          playlist: state.originalPlaylist,
          isShuffling: false,
          currentIndex: originalIndex !== -1 ? originalIndex : 0,
        };
      } else {
        // Enable shuffle: randomize the playlist
        const shuffledPlaylist = [...state.originalPlaylist].sort(() => Math.random() - 0.5);
        const newIndex = shuffledPlaylist.findIndex((t) => t.id === state.currentTrack?.id);
        return {
          ...state,
          playlist: shuffledPlaylist,
          isShuffling: true,
          currentIndex: newIndex !== -1 ? newIndex : 0,
        };
      }

    case "SET_PROGRESS":
      return { ...state, progress: action.progress };

    case "STOP":
      return { ...state, isPlaying: false, progress: 0 };

    case "SET_CURRENT_TRACK_AUDIO_URL":
      if (!state.currentTrack) return state;
      return {
        ...state,
        currentTrack: { ...state.currentTrack, audioUrl: action.audioUrl },
      };

    // Add a new case to update the duration if needed
    case "SET_DURATION":
      return { ...state, duration: action.duration };
      
    default:
      return state;
  }
};

// Helper function to shuffle array
function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    // Simple type assertion to tell TypeScript these values exist
    [newArray[i], newArray[j]] = [newArray[j] as T, newArray[i] as T];
  }
  return newArray;
}







